import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  FlatList,
} from 'react-native';
import { useNavigation, useTheme } from '@react-navigation/native';
import { SearchBar, Icon, Header, Button, CategoryItemSkeletonList } from '@components';
import { Theme } from '@theme';
import { Category, usePaginateCategories, useScreen } from '@hooks';
import { defaultImage } from '@assets';
import { getCommonStyles } from '@utils';
import EditCategory from './EditCategory';
import CategoryDetails from './CategoryDetails';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLanguage } from '@context';

export const Categories: React.FC = () => {
  const navigation = useNavigation<any>();
  const theme = useTheme();
  const commonStyles = getCommonStyles(theme);
  const { t } = useLanguage();
  const styles = getStyles(theme);
  const {isWeb, isMobile} = useScreen();
  const [searchText, setSearchText] = useState('');
  
  const { flatData: categories, fetchNextPage, hasNextPage, isFetchingNextPage, totalCount, isLoading } = usePaginateCategories(searchText);

  const [categoryId, setCategoryId] = useState<string | null>(categories?.[0]?.id || null);
  const [isEdit, setIsEdit] = useState(false);


  const navigateToCategoryDetails = (categoryId: string) => {
    if(isMobile) {
      navigation.navigate('EditCategory', { categoryId });
    } else {
      setIsEdit(false);
      setCategoryId(categoryId);
    }
    /*navigation.navigate('CategoryDetails', { categoryId: category.id });*/
  };

  const handleAddCategory = () => {
    setIsEdit(false);
    navigation.navigate('AddCategory');
  };

  const renderCategoryItem = ({ item }: { item: Category }) => (
    <TouchableOpacity
      style={[styles.categoryItem, !isMobile && categoryId === item.id && {backgroundColor: theme.colors.grey10}]}
      onPress={() => navigateToCategoryDetails(item.id)}
      activeOpacity={0.7}
    >
      <View style={styles.categoryContent}>
        <Image source={!item.avatarUrl? defaultImage : { uri: item.avatarUrl}} style={styles.categoryIcon} />
        <View style={styles.categoryInfo}>
          <Text style={styles.categoryName}>{item.name}</Text>
          <Text style={styles.categorySubtitle}>{t('common.personCount', { count: item.contactAccountIds?.length || 0 })}</Text>
          {/*<Text style={styles.categorySubtitle}>
            {category.subcategories.length} sous intérêt{category.subcategories.length > 1 ? 's' : ''}
          </Text> TODO*/}
        </View>
      </View>
      <Icon name="arrow" size={theme.iconSize.large} color={theme.colors.greyBlue} />
    </TouchableOpacity>
  );

  const handleEndReached = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={commonStyles.flex}>
        <Header
          title={t('categoriesScreen.title')}
          iconLeft={isWeb? undefined : "arrowBack"}
          iconRight={isMobile? "plus" : undefined}
          onPressRight={handleAddCategory}
        />
        <View style={[commonStyles.rowBetween, commonStyles.alignStart]}>
          <View style={commonStyles.flex}>
            <View style={[styles.searchContainer, !isMobile && commonStyles.searchContainerMaxWidth]}>
              <SearchBar value={searchText} onChangeText={setSearchText} />
            </View>

            <Text style={styles.sectionTitle}>{t('common.resultsCount', { count: totalCount })}</Text>
          </View>
          {!isMobile && <Button isSmall style={[styles.buttonAdd, !isMobile && commonStyles.alignSelfEnd]}  title={t('categoriesScreen.newCategory')} textColor={theme.colors.primary} iconName="plus" iconColor={theme.colors.primary} onPress={handleAddCategory} />}
        </View>
        <View style={[commonStyles.flex, commonStyles.rowGapLg, commonStyles.alignStart]}>
          {isLoading ? (
            <View style={[isMobile? styles.categoriesList : commonStyles.flex315, commonStyles.fullHeight]}>
              <CategoryItemSkeletonList count={8} />
            </View>
          ) : (
            <FlatList
              data={categories}
              renderItem={renderCategoryItem}
              keyExtractor={(item) => item.id}
              onEndReached={handleEndReached}
              onEndReachedThreshold={0.5}
              showsVerticalScrollIndicator={false}
              style={[isMobile? styles.categoriesList : commonStyles.flex315, commonStyles.fullHeight]}
            />
          )}
          {!isMobile && !!categoryId && <View style={styles.columnDivider} />}
          {!isMobile && !!categoryId && !isEdit && <View style={[commonStyles.flex, commonStyles.fullHeight]}>
            <CategoryDetails categoryId={categoryId} onEdit={() => setIsEdit(true)} />
          </View>}
          {!isMobile && !!categoryId && isEdit && <View style={[commonStyles.flex, commonStyles.fullHeight]}>
            <EditCategory categoryId={categoryId} onBack={() => setIsEdit(false)} onDeleted={() => { setIsEdit(false); setCategoryId(null); }} />
          </View>}
        </View>
      </View>
    </SafeAreaView>
  );
};

const getStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
  },
  searchContainer: {
    paddingHorizontal: theme.spacing.md,
    backgroundColor: theme.colors.white,
  },
  buttonAdd: {
    backgroundColor: theme.colors.white,
    marginRight: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.fontSize.extraSmall,
    color: theme.colors.greyBlue,
    fontWeight: '400',
    paddingHorizontal: theme.spacing.lg,
    paddingTop: theme.spacing.lg,
    paddingBottom: theme.spacing.xs,
  },
  categoriesList: {
    flex: 1,
  },
  categoryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.grey,
  },
  categoryContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  categoryIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.lg,
  },
  categoryInfo: {
    flex: 1,
  },
  // Thin vertical line separating the list column from the detail column (web).
  columnDivider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: theme.colors.grey,
  },
  categoryName: {
    fontSize: theme.fontSize.medium,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: theme.spacing.xs,
  },
  categorySubtitle: {
    fontSize: theme.fontSize.small,
    color: theme.colors.greyBlue,
    fontWeight: '400',
  },
});

export default Categories; 